import { useRef } from 'react';
import packageJson from '../../package.json';
import useEnv from './useEnv';

// Set the name of the hidden property and the change event for visibility
let visibilityChange = '';
if (document.hidden !== undefined) { // Opera 12.10 and Firefox 18 and later support
    visibilityChange = 'visibilitychange';
} else if (document.msHidden !== undefined) {
    visibilityChange = 'msvisibilitychange';
} else if (document.webkitHidden !== undefined) {
    visibilityChange = 'webkitvisibilitychange';
}

const MAX_CHECK_INTERVAL = 60000; // 1 minute

const useReloader = (confirmText) => {
    const { isProduction } = useEnv();
    const timeoutRef = useRef(null);
    const lastCheckTimeRef = useRef(0);

    /**
     * Reload the page.
     * @param {boolean} clearCache Also clear Cache Storage before reloading (default: false)
     */
    const reload = async (clearCache = false) => {
        if (clearCache && typeof caches !== 'undefined') {
            // Service worker cache should be cleared with caches.delete()
            try {
                const names = await caches.keys();
                await Promise.all(names.map(name => caches.delete(name)));
            } catch (error) {
                // Reload anyway; a stale cache is better than a stuck page
            }
        }
        // the (non-standard) true argument makes Firefox bypass the browser cache
        window.location.reload(clearCache);
    }

    const handleVisibilityChange = () => {
        if (!document.hasFocus()) {
            return;
        }
        // Debounce multiple calls
        if (timeoutRef.current !== null) {
            window.clearInterval(timeoutRef.current);
        }
        timeoutRef.current = window.setTimeout(onFocus, 500);
    }

    const onFocus = () => {
        if ((Date.now() - lastCheckTimeRef.current) < MAX_CHECK_INTERVAL) {
            return;
        }
        fetch(`/meta.json?t=${Date.now()}`)
            .then(response => response.json())
            .then(meta => {
                const latestVersion = meta.version;
                if (latestVersion !== undefined && latestVersion.toString() !== packageJson.version.toString()){
                    lastCheckTimeRef.current = Date.now();
                    if (window.confirm(confirmText)) {
                        reload(true);
                    }
                }
            }).catch(error => {
                if (!isProduction) {
                    console.error('Error fetching meta.json:', error);
                }
            })
    }

    const initReloadListeners = () => {
        if (!isProduction) {
            console.log('Reloader not used in dev or testing');
            return;
        }
        if (document.addEventListener !== undefined) {
            document.addEventListener(visibilityChange, handleVisibilityChange, false);
        }
        window.addEventListener("focus", handleVisibilityChange, false);
        window.addEventListener("pageshow", handleVisibilityChange, false); // For iOS
        return () => {
            document.removeEventListener(visibilityChange, handleVisibilityChange);
            window.removeEventListener("focus", handleVisibilityChange);
            window.removeEventListener("pageshow", handleVisibilityChange);
        }
    }

    return {
        initReloadListeners,
        reload
    }
}

export default useReloader;
